import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import React, { ReactNode, useCallback, useState } from 'react';

interface AccordionWrapperProps {
  title: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  // Optional controlled props
  expanded?: boolean;
  onChange?: () => void;
  // New props for sticky header behavior
  stickyHeader?: boolean;
  stickyTop?: string;
  headerColor?: string;
  detailsPadding?: string;
  headerPadding?: string;
}

const AccordionWrapper: React.FC<AccordionWrapperProps> = ({
  title,
  children,
  defaultExpanded = true,
  expanded,
  onChange,
  stickyHeader = false,
  stickyTop = '0px',
  headerColor = '#f5f5f5',
  detailsPadding = '8px 16px',
  headerPadding = '0 16px',
}) => {
  // Determine if component is being used in controlled mode
  const isControlled = expanded !== undefined && onChange !== undefined;

  // Internal state for uncontrolled mode
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Memoize expand toggle to avoid re-creation on each render
  const handleChange = useCallback(() => {
    if (isControlled) {
      onChange?.();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  }, [isControlled, onChange, internalExpanded]);

  // Use controlled expanded value if provided, otherwise use internal state
  const isExpanded = isControlled ? expanded : internalExpanded;

  return (
    <Box sx={{ width: '100%', marginBottom: 0 }}>
      <Accordion
        expanded={isExpanded}
        onChange={handleChange}
        sx={{
          borderRadius: '8px',
          boxShadow: 'none',
          border: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="content"
          id="header"
          sx={{
            borderBottom: '1px solid #e0e0e0',
            minHeight: '56px',
            backgroundColor: headerColor,
            borderRadius: '8px',
            padding: headerPadding, // Use custom padding if provided
            // Apply sticky positioning to header when stickyHeader is true
            ...(stickyHeader &&
              isExpanded && {
                position: 'sticky',
                top: stickyTop,
                zIndex: 85,
              }),
            '&.Mui-expanded': {
              minHeight: '56px',
              margin: 0,
            },
            '& .MuiAccordionSummary-content': {
              margin: '2px 0',
              alignItems: 'center',
              '&.Mui-expanded': {
                margin: '2px 0',
              },
            },
          }}
        >
          {typeof title === 'string' ? (
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#000000' }}>
              {title}
            </Typography>
          ) : (
            title
          )}
        </AccordionSummary>

        <AccordionDetails
          sx={{
            fontFamily: 'poppins',
            padding: detailsPadding, // Use custom padding if provided
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionWrapper;
